import type { RetrieveSystemDesignResponse } from '../../services/oneButtonServices/designServices';
import type { PriceQuoteOverrides } from '../../store/oneButtonStore';
import type { PreContractIntakeForm } from './preContractTypes';

type RoofRecord = Record<string, unknown>;

export type PreContractRoofDesignSnapshot = {
    low: number;
    normal: number;
    total: number;
    configured: boolean;
    manufacturer: string | null;
    roofProduct: string | null;
    shingleColor: string | null;
    pricePerSqft: number | null;
    minSolarRate: number | null;
    proposedSolarRate: number | null;
};

function finiteNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function pickRoofRecord(systemDesign: RetrieveSystemDesignResponse | null): RoofRecord | null {
    if (!systemDesign) return null;
    const interest = systemDesign.OpportunityInterest as Record<string, unknown> | undefined;
    const candidates: unknown[] = [];
    if (interest?.Roof || interest?.RRR) {
        candidates.push(systemDesign.Roof);
    }
    if (interest?.RoofOnly) {
        candidates.push(systemDesign.RoofOnly);
    }
    candidates.push(systemDesign.Roof, systemDesign.RoofOnly);

    for (const candidate of candidates) {
        if (!candidate) continue;
        if (Array.isArray(candidate)) {
            const first = candidate.find((row) => row && typeof row === 'object');
            if (first && typeof first === 'object') return first as RoofRecord;
            continue;
        }
        if (typeof candidate === 'object') return candidate as RoofRecord;
    }
    return null;
}

function productLineLabel(manufacturer: string | null, roofProduct: string | null): string | null {
    const parts = [manufacturer, roofProduct].map((part) => String(part ?? '').trim()).filter(Boolean);
    if (parts.length === 0) return null;
    return parts.join(' ');
}

export function buildPreContractRoofDesignSnapshot(input: {
    form: PreContractIntakeForm;
    systemDesign?: RetrieveSystemDesignResponse | null;
    priceQuoteOverrides?: PriceQuoteOverrides | null;
}): PreContractRoofDesignSnapshot {
    const roof = pickRoofRecord(input.systemDesign ?? null);
    const overrides = input.priceQuoteOverrides ?? null;

    const overrideLow =
        finiteNumber(overrides?.lowSlopeOverride) ??
        finiteNumber(input.form.roofSlopeLowOverride) ??
        finiteNumber(roof?.Low_Slope_Override);
    const overrideNormal =
        finiteNumber(overrides?.normalSlopeOverride) ??
        finiteNumber(input.form.roofSlopeNormalOverride) ??
        finiteNumber(roof?.Normal_Slope_Override);

    let low = overrideLow ?? finiteNumber(input.form.roofSlopeLowTotal) ?? 0;
    let normal = overrideNormal ?? finiteNumber(input.form.roofSlopeNormalTotal) ?? 0;

    const overrideTotal = finiteNumber(overrides?.roofSquareOverride) ?? finiteNumber(roof?.RoofSquareOverride);
    if (low + normal <= 0 && overrideTotal != null && overrideTotal > 0) {
        if (overrideLow != null || overrideNormal != null) {
            low = overrideLow ?? 0;
            normal = overrideNormal ?? Math.max(overrideTotal - low, 0);
        } else {
            normal = overrideTotal;
        }
    }

    const total = low + normal;

    const manufacturer = String(roof?.Manufacturer ?? '').trim() || null;
    const roofProduct = String(roof?.RoofProduct ?? '').trim() || null;
    const shingleColor =
        String(overrides?.selectedShingleAlt ?? roof?.ShingleColor ?? '').trim() || null;
    const pricePerSqft =
        finiteNumber(overrides?.pricePerSqFt) ?? finiteNumber(roof?.PricePerSqft) ?? null;
    const minSolarRate =
        finiteNumber(overrides?.minSolarRate) ?? finiteNumber(roof?.MinRate) ?? null;
    const proposedSolarRate =
        finiteNumber(overrides?.proposedSolarRate) ?? finiteNumber(roof?.ProposedRate) ?? null;

    const configured =
        total > 0 ||
        Boolean(productLineLabel(manufacturer, roofProduct)) ||
        Boolean(shingleColor) ||
        (pricePerSqft != null && pricePerSqft > 0);

    return {
        low,
        normal,
        total,
        configured,
        manufacturer,
        roofProduct,
        shingleColor,
        pricePerSqft,
        minSolarRate,
        proposedSolarRate,
    };
}

export function formatRoofPricePerSqft(value: number | null | undefined): string | null {
    if (value == null || !Number.isFinite(value) || value <= 0) return null;
    const digits = value < 1 ? 3 : 2;
    return `$${value.toFixed(digits)}/sq ft`;
}

export function formatRoofProductLine(snapshot: PreContractRoofDesignSnapshot): string | null {
    return productLineLabel(snapshot.manufacturer, snapshot.roofProduct);
}

/** Patch intake form slope fields from saved roof design / quote overrides. */
export function patchFormFromRoofDesignSnapshot(
    form: PreContractIntakeForm,
    snapshot: PreContractRoofDesignSnapshot,
    sourceOverrides?: PriceQuoteOverrides | null,
    systemDesign?: RetrieveSystemDesignResponse | null,
): Partial<PreContractIntakeForm> | null {
    const roofRecord = pickRoofRecord(systemDesign ?? null);
    const patch: Partial<PreContractIntakeForm> = {};
    let changed = false;

    const lowOverride =
        sourceOverrides?.lowSlopeOverride !== undefined
            ? sourceOverrides.lowSlopeOverride
            : finiteNumber(roofRecord?.Low_Slope_Override);
    const normalOverride =
        sourceOverrides?.normalSlopeOverride !== undefined
            ? sourceOverrides.normalSlopeOverride
            : finiteNumber(roofRecord?.Normal_Slope_Override);

    if (lowOverride !== undefined && lowOverride !== form.roofSlopeLowOverride) {
        patch.roofSlopeLowOverride = lowOverride;
        changed = true;
    }
    if (normalOverride !== undefined && normalOverride !== form.roofSlopeNormalOverride) {
        patch.roofSlopeNormalOverride = normalOverride;
        changed = true;
    }

    if (!changed && snapshot.configured) {
        if (snapshot.low > 0 && form.roofSlopeLowOverride == null && lowOverride == null) {
            patch.roofSlopeLowOverride = snapshot.low;
            changed = true;
        }
        if (snapshot.normal > 0 && form.roofSlopeNormalOverride == null && normalOverride == null) {
            patch.roofSlopeNormalOverride = snapshot.normal;
            changed = true;
        }
    }

    return changed ? patch : null;
}
